import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    Divider,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { APIURL } from '../../config';
import EmpSettings from '../../components/Settings/Employee/EmpSettings';
import Onboarding from '../../components/Settings/Employee/Onboarding';
import EmployeeRelationship from '../../components/Settings/Employee/EmployeeRelationship';
import CustomerAssignment from '../../components/Settings/Employee/CustomerAssignment';
import Scheduler from '../Scheduler/Scheduler';

const EmployeePage = (props) => {
    const [activeTab, setActiveTab] = useState('employees');
    const [employees, setEmployees] = useState([]);
    const [dropdowns, setDropdowns] = useState({ empType: [], compensationType: [], empStatus: [] });
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [detailEmployee, setDetailEmployee] = useState(null);
    const [scheduleEmployeeId, setScheduleEmployeeId] = useState(props.user.emp_id ?? '');

    const canManageEmployees = Boolean(props.user?.permissions?.SettingsEmployeeTab);
    const canUseAdminTools = Boolean(props.user?.permissions?.InviteEmployees);
    const hasEmpIsAdminFlag = [true, 1, '1', 'true'].includes(props.user?.emp_is_admin);
    const isAdminEmployeeType = String(props.user?.emp_type ?? props.user?.type).trim().toLowerCase() === 'admin';
    const hasSpecialAdminAccess = hasEmpIsAdminFlag && !isAdminEmployeeType;
    const isEmployeeAdmin = canManageEmployees || isAdminEmployeeType || hasEmpIsAdminFlag;
    const canAccessCustomerAssignment = isEmployeeAdmin && !hasSpecialAdminAccess;
    const businessType = props.user?.comp_settings?.find(
        (setting) => setting.feature_value === 'primaryBusinessType'
    )?.feature_id ?? -1;

    useEffect(() => {
        let active = true;

        const loadEmployees = async () => {
            setLoading(true);
            setError('');
            try {
                const requests = [
                    axios.get(`${APIURL}/employees`, { params: { com_id: props.comp_id } }),
                    axios.get(`${APIURL}/dropdowns/dropdownType/empType`, { params: { comp_id: props.comp_id } }),
                    axios.get(`${APIURL}/dropdowns/dropdownType/salaryType`, { params: { comp_id: props.comp_id } }),
                    axios.get(`${APIURL}/dropdowns/dropdownType/empStatus`, { params: { comp_id: props.comp_id } }),
                ];

                if (canUseAdminTools) {
                    requests.push(axios.get(`${APIURL}/activity_log`, { params: { empID: props.user.emp_id } }));
                }

                const [employeeResponse, empTypeResponse, compensationResponse, statusResponse, inviteResponse] = await Promise.all(requests);
                if (!active) return;

                setEmployees(Array.isArray(employeeResponse.data) ? employeeResponse.data : []);
                setDropdowns({
                    empType: Array.isArray(empTypeResponse.data) ? empTypeResponse.data : [],
                    compensationType: Array.isArray(compensationResponse.data) ? compensationResponse.data : [],
                    empStatus: Array.isArray(statusResponse.data) ? statusResponse.data : [],
                });
                setInvites(Array.isArray(inviteResponse?.data) ? inviteResponse.data : []);
            } catch (loadError) {
                console.error('Unable to load employee page', loadError);
                if (active) setError('Employee information could not be loaded. Please try again.');
            } finally {
                if (active) setLoading(false);
            }
        };

        loadEmployees();
        return () => { active = false; };
    }, [props.comp_id, props.user.emp_id, canUseAdminTools]);

    const visibleEmployees = useMemo(() => {
        if (isEmployeeAdmin) return employees;
        return employees.filter((employee) => String(employee.emp_id) === String(props.user.emp_id));
    }, [employees, isEmployeeAdmin, props.user.emp_id]);

    const displayedEmployee = detailEmployee || (!isEmployeeAdmin ? visibleEmployees[0] : null);
    const scheduleEmployee = visibleEmployees.find(
        (employee) => String(employee.emp_id) === String(scheduleEmployeeId)
    ) || null;

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box
            sx={{
                mx: 'auto',
                width: '95%',
                height: 'calc(100vh - 100px)',
                minHeight: 560,
                pb: 2,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h4">Employees</Typography>
                        {displayedEmployee && (
                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                                / {displayedEmployee.emp_first_name} {displayedEmployee.emp_last_name}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {displayedEmployee
                            ? 'Review and manage this employee’s information.'
                            : 'Manage employee information, assignments, and work details.'}
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

            {!detailEmployee && (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(event, value) => setActiveTab(value)}
                        TabIndicatorProps={{
                            sx: {
                                backgroundColor: hasSpecialAdminAccess && activeTab === 'employees'
                                    ? 'error.main'
                                    : 'primary.main',
                            },
                        }}
                    >
                        <Tab
                            value="employees"
                            label={!isEmployeeAdmin ? 'My Profile' : hasSpecialAdminAccess ? (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <WarningAmberIcon fontSize="small" />
                                    Employee List
                                </Box>
                            ) : 'Employee List'}
                            sx={hasSpecialAdminAccess ? {
                                color: 'error.main',
                                '&.Mui-selected': { color: 'error.main' },
                            } : undefined}
                        />
                        {canAccessCustomerAssignment && <Tab value="customer-assignment" label="Customer Relationships" />}
                        {isEmployeeAdmin && canUseAdminTools && <Tab value="relationships" label="Employee Relationships" />}
                        {isEmployeeAdmin && canUseAdminTools && <Tab value="onboarding" label="Onboard Employee" />}
                        <Tab value="schedule" label="Employee Schedule" />
                    </Tabs>
                    <Divider sx={{ mb: 2 }} />
                </>
            )}

            <Box sx={{ flex: 1, minHeight: 0, overflow: activeTab === 'employees' ? 'hidden' : 'auto' }}>
                {activeTab === 'schedule' && (
                    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {isEmployeeAdmin && (
                            <Autocomplete
                                options={visibleEmployees}
                                value={scheduleEmployee}
                                onChange={(event, employee) => setScheduleEmployeeId(employee?.emp_id ?? '')}
                                getOptionLabel={(employee) => `${employee.emp_first_name || ''} ${employee.emp_last_name || ''}`.trim()}
                                isOptionEqualToValue={(option, value) => String(option.emp_id) === String(value.emp_id)}
                                renderInput={(params) => <TextField {...params} label="Employee" size="small" />}
                                sx={{ width: 320, mb: 2 }}
                            />
                        )}
                        {scheduleEmployee ? (
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                <Scheduler
                                    {...props}
                                    user={props.user}
                                    comp_id={props.comp_id}
                                    selectedView="employee"
                                    selectedEmployeeId={scheduleEmployee.emp_id}
                                />
                            </Box>
                        ) : (
                            <Alert severity="info">Select an employee to view their schedule.</Alert>
                        )}
                    </Box>
                )}
                {activeTab === 'employees' && (
                    <EmpSettings
                        {...props}
                        user={props.user}
                        user_id={props.user.emp_id}
                        comp_id={props.comp_id}
                        empList={visibleEmployees}
                        assignmentEmpList={employees}
                        levelList={dropdowns.empType}
                        compensationType={dropdowns.compensationType}
                        statusList={dropdowns.empStatus}
                        type={props.user.type}
                        businessType={businessType}
                        canManageEmployees={isEmployeeAdmin}
                        canViewInternalSupport={props.user?.emp_type === 'Admin'}
                        selfServiceMode={!isEmployeeAdmin}
                        onDetailViewChange={setDetailEmployee}
                        onDesignationChange={(employeeIds, designation) => setEmployees((current) => current.map((employee) =>
                            employeeIds.includes(employee.emp_id) ? { ...employee, emp_type: designation } : employee
                        ))}
                        leftColumnSize={4}
                        rightColumnSize={8}
                    />
                )}
                {activeTab === 'customer-assignment' && canAccessCustomerAssignment && (
                    <CustomerAssignment
                        comp_id={props.comp_id}
                        empList={employees}
                        leftColumnSize={3}
                        rightColumnSize={9}
                    />
                )}
                {activeTab === 'relationships' && canUseAdminTools && (
                    <EmployeeRelationship
                        leftColumnSize={3}
                        rightColumnSize={9}
                        user_id={props.user.emp_id}
                        comp_id={props.comp_id}
                        empList={employees}
                        empType={dropdowns.empType}
                    />
                )}
                {activeTab === 'onboarding' && canUseAdminTools && (
                    <Onboarding
                        leftColumnSize={3}
                        rightColumnSize={9}
                        user={props.user}
                        comp_id={props.comp_id}
                        invites={invites}
                    />
                )}
            </Box>
        </Box>
    );
};

export default EmployeePage;
